import { browserSupportsWebAuthn, startAuthentication, WebAuthnAbortService, WebAuthnError, type PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import type { Status } from '../../shared/pruefpunkte.js';
import { api, fehlerText, modus } from '../api.js';
import { abschnitt, h, knopf, liste, meldung, schritte, setzeMeldung, zeile } from '../dom.js';
import { ergebnisVon, melde } from '../ergebnisse.js';
import type { Ansicht } from '../router.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';
import { clientFehlerText, serverAntwort } from './passkey.js';

/**
 * Passkey im Anmeldefeld (Konzept, Anmeldung und Schutz vor Bots): Das Feld trägt
 * autocomplete="username webauthn", und eine bedingte Anmeldung
 * (mediation: 'conditional') wartet im Hintergrund. Das Gerät soll den Passkey
 * dann gleich über der Tastatur bzw. am Feld anbieten, ohne eigenen Knopf.
 */

type Verfuegbarkeit = 'ja' | 'nein' | 'fehlt';
type Angebot = 'ja' | 'umweg' | 'nein';

/** Die Challenge gilt 5 Minuten; vorher startet die bedingte Anmeldung mit einer neuen. */
const ERNEUERN_MS = 4 * 60_000;
/** Nach so vielen Fehlern in Folge hält die Ansicht an, statt das Gerät mit Anfragen zu bedrängen. */
const MAX_FEHLER_IN_FOLGE = 5;

async function bedingteAnmeldung(): Promise<Verfuegbarkeit> {
  if (!browserSupportsWebAuthn() || typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function') return 'fehlt';
  try {
    return (await PublicKeyCredential.isConditionalMediationAvailable()) ? 'ja' : 'nein';
  } catch {
    return 'nein';
  }
}

/** conditionalGet aus getClientCapabilities, ohne Ersatz aus anderen Abfragen (die stehen getrennt daneben). */
async function conditionalGet(): Promise<'ja' | 'nein' | 'unbekannt' | 'fehlt'> {
  if (!browserSupportsWebAuthn() || typeof PublicKeyCredential.getClientCapabilities !== 'function') return 'fehlt';
  try {
    const wert = (await PublicKeyCredential.getClientCapabilities()).conditionalGet;
    return wert === true ? 'ja' : wert === false ? 'nein' : 'unbekannt';
  } catch {
    return 'unbekannt';
  }
}

function istAbbruch(fehler: unknown): boolean {
  return (fehler instanceof WebAuthnError && fehler.code === 'ERROR_CEREMONY_ABORTED') || (fehler instanceof Error && fehler.name === 'AbortError');
}

function warte(ms: number): Promise<void> {
  return new Promise((fertig) => window.setTimeout(fertig, ms));
}

/**
 * Hält die bedingte Anmeldung offen, solange die Ansicht zu sehen ist. Sie startet
 * alle 4 Minuten mit einer frischen Challenge neu, aber nie, während das Feld den
 * Fokus hat: Sonst verschwände der Vorschlag gerade dann, wenn er gebraucht wird.
 * Kommt die App aus dem Hintergrund zurück, in dem Zeitgeber ruhen, startet sie
 * ebenfalls neu, wenn die Challenge zu alt ist. Nach einer Anmeldung hält sie an (ein Browser ohne Nachfrage, etwa mit
 * virtuellem Authenticator, meldete sonst endlos weiter an); „Noch einmal
 * versuchen“ stellt den Vorschlag wieder bereit.
 */
function vorschlagBereitstellen(feld: HTMLInputElement, status: HTMLElement, aufraeumer: Aufraeumer): HTMLElement {
  let beendet = false;
  let laeuft = false;
  let erneuern: number | undefined;
  let bereitSeit = 0;
  let letzterFehler = '';
  const nochmal = h('div', { hidden: true });

  const planeErneuerung = (ms: number) => {
    window.clearTimeout(erneuern);
    erneuern = window.setTimeout(() => {
      if (document.activeElement === feld) planeErneuerung(30_000);
      else WebAuthnAbortService.cancelCeremony();
    }, ms);
  };

  /** Eine Runde endet mit einer Anmeldung (gelungen oder vom Server abgelehnt) oder nach zu vielen Fehlern. */
  const runde = async (): Promise<void> => {
    let fehlerInFolge = 0;
    while (!beendet) {
      let optionen: PublicKeyCredentialRequestOptionsJSON;
      try {
        optionen = await api<PublicKeyCredentialRequestOptionsJSON>('/api/passkey/feld/optionen', { methode: 'POST' });
      } catch (fehler) {
        setzeMeldung(status, `Vorschlag nicht bereit: ${fehlerText(fehler)} Neuer Versuch in 10\u00a0Sekunden.`, 'fehler');
        await warte(10_000);
        continue;
      }
      if (beendet) return;
      bereitSeit = Date.now();
      planeErneuerung(ERNEUERN_MS);
      try {
        const antwort = await startAuthentication({ optionsJSON: optionen, useBrowserAutofill: true });
        window.clearTimeout(erneuern);
        await serverAntwort('/api/passkey/anmeldung', { antwort, zweck: 'feld' }, status);
        return;
      } catch (fehler) {
        window.clearTimeout(erneuern);
        if (beendet) return;
        // Eigener Abbruch zum Erneuern: gleich mit frischer Challenge weiter.
        if (istAbbruch(fehler)) continue;
        fehlerInFolge += 1;
        const text = clientFehlerText(fehler);
        if (text !== letzterFehler) {
          letzterFehler = text;
          await melde('passkey-feld', 'fehler', `Vorschlag im Anmeldefeld: ${text}`, { fehler: (fehler as Error).name, modus: modus(), mediation: 'conditional' });
        }
        if (fehlerInFolge >= MAX_FEHLER_IN_FOLGE) {
          setzeMeldung(status, `${text} Der Vorschlag ist angehalten.`, 'fehler');
          return;
        }
        setzeMeldung(status, `${text} Der Vorschlag ist gleich wieder bereit.`, 'fehler');
        await warte(2_000);
      }
    }
  };

  const starte = async (): Promise<void> => {
    if (laeuft || beendet) return;
    laeuft = true;
    nochmal.hidden = true;
    setzeMeldung(status, 'Bereit: Ins Feld tippen.');
    try {
      await runde();
    } finally {
      laeuft = false;
      if (!beendet) nochmal.hidden = false;
    }
  };

  nochmal.append(knopf('Noch einmal versuchen', () => void starte(), 'sekundaer'));
  aufraeumer.hoere(document, 'visibilitychange', () => {
    if (document.visibilityState === 'visible' && laeuft && Date.now() - bereitSeit > ERNEUERN_MS && document.activeElement !== feld) {
      WebAuthnAbortService.cancelCeremony();
    }
  });
  aufraeumer.dazu(() => {
    beendet = true;
    window.clearTimeout(erneuern);
    WebAuthnAbortService.cancelCeremony();
  });
  void starte();
  return nochmal;
}

/** Hält fest, ob der Browser den Vorschlag kann; ein Ergebnis aus einer Anmeldung oder einem Fehler bleibt stehen. */
async function meldeVerfuegbarkeit(verfuegbar: Verfuegbarkeit, faehigkeit: string): Promise<void> {
  const bisher = ergebnisVon('passkey-feld');
  const hier = bisher?.modus === modus() ? bisher : undefined;
  if (hier && (hier.status === 'ok' || hier.status === 'fehler' || hier.details?.bedingt === verfuegbar)) return;
  const details = { bedingt: verfuegbar, conditionalGet: faehigkeit, modus: modus() };
  if (verfuegbar === 'ja') {
    await melde('passkey-feld', 'offen', 'Der Browser kann den Passkey im Anmeldefeld anbieten (bedingte Anmeldung verfügbar). Noch nicht darüber angemeldet.', details);
  } else {
    await melde(
      'passkey-feld',
      'nicht_unterstuetzt',
      verfuegbar === 'fehlt'
        ? 'Der Browser kennt die bedingte Anmeldung nicht (isConditionalMediationAvailable fehlt).'
        : 'Der Browser meldet: kein Passkey-Vorschlag im Anmeldefeld (isConditionalMediationAvailable = false).',
      details,
    );
  }
}

export const anmeldefeld: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  const verfuegbar = await bedingteAnmeldung();
  const faehigkeit = await conditionalGet();
  const status = meldung();
  const statusFrage = meldung();
  /** Platzhalter für „Noch einmal versuchen“, solange der Vorschlag nicht läuft */
  const nochmal = h('div', { hidden: true });

  const feld = h('input', {
    id: 'anmeldename',
    name: 'username',
    type: 'text',
    inputmode: 'email',
    autocomplete: 'username webauthn',
    autocapitalize: 'none',
    autocorrect: 'off',
    spellcheck: 'false',
    'aria-describedby': 'anmeldename-hilfe',
  });
  const formular = h(
    'form',
    { class: 'formular', novalidate: true },
    h(
      'div',
      { class: 'feld' },
      h('label', { for: 'anmeldename' }, 'E-Mail oder Benutzername'),
      feld,
      h('p', { id: 'anmeldename-hilfe', class: 'hilfe' }, 'Nur für den Test: nichts eintippen, sondern den angebotenen Passkey wählen.'),
    ),
    h('div', {}, h('button', { type: 'submit', class: 'knopf knopf-sekundaer' }, 'Mit Passwort weiter')),
  );
  formular.addEventListener('submit', (ereignis) => {
    ereignis.preventDefault();
    setzeMeldung(status, 'Im echten Login ginge es jetzt mit dem Passwort weiter. Für diesen Test bitte den angebotenen Passkey wählen.');
  });

  const antworte = async (angebot: Angebot): Promise<void> => {
    const feldErgebnis = ergebnisVon('passkey-feld');
    const angemeldet = feldErgebnis?.modus === modus() && feldErgebnis.status === 'ok';
    const details = { angeboten: angebot, bedingt: verfuegbar, conditionalGet: faehigkeit, angemeldetUeberFeld: angemeldet, modus: modus(), quelle: 'von Hand' };
    let ergebnis: Status;
    let text: string;
    if (angebot === 'ja') {
      ergebnis = 'ok';
      text = `Passkey im Anmeldefeld angeboten (laut Angabe)${angemeldet ? ' und damit angemeldet' : ''}.`;
    } else if (angebot === 'umweg') {
      ergebnis = 'fehler';
      text = 'Passkey nur über einen Umweg angeboten (laut Angabe), nicht gleich am Feld. Dann braucht die Anmeldung einen eigenen Passkey-Knopf.';
    } else {
      ergebnis = verfuegbar === 'ja' ? 'fehler' : 'nicht_unterstuetzt';
      text = 'Kein Passkey im Anmeldefeld angeboten (laut Angabe).';
    }
    await melde('passkey-vorschlag', ergebnis, text, details);
    setzeMeldung(statusFrage, 'Gespeichert.', 'ok');
  };

  ziel.append(
    ...kopf('Passkey im Anmeldefeld', 'Bietet das Gerät den Passkey gleich im Anmeldefeld an, ohne eigenen Knopf? (Konzept, Anmeldung und Schutz vor Bots)'),
    h('p', { class: 'einleitung' }, 'Das Feld trägt ', h('span', { class: 'code', translate: 'no' }, 'autocomplete="username webauthn"'), '; im Hintergrund wartet eine bedingte Anmeldung.'),
    abschnitt(
      'Anmelden',
      pruefpunktStand('passkey-feld', aufraeumer),
      schritte(
        'Unter „Passkey“ zuerst einen Passkey anlegen, falls es noch keinen gibt.',
        'Ins Feld „E-Mail oder Benutzername“ tippen. iPhone: Der Passkey sollte gleich über der Tastatur stehen. Android und PC: direkt am Feld.',
        'Den Passkey antippen und mit Face ID, Fingerabdruck oder Windows Hello bestätigen.',
        'Unten die Frage beantworten, auch wenn nichts angeboten wurde.',
      ),
      formular,
      status,
      nochmal,
    ),
    abschnitt(
      'Frage',
      pruefpunktStand('passkey-vorschlag', aufraeumer),
      h(
        'div',
        { class: 'frage', role: 'group', 'aria-labelledby': 'frage-vorschlag', 'aria-describedby': 'frage-vorschlag-hilfe' },
        h('p', { id: 'frage-vorschlag' }, 'Wurde der Passkey über der Tastatur bzw. im Feld angeboten?'),
        h('p', { id: 'frage-vorschlag-hilfe', class: 'hilfe' }, '„Nur über Umweg“: erst nach Tippen auf ein Schlüssel-Symbol, auf „Passwörter“ oder in einem Menü.'),
        knopf('Ja, gleich angeboten', () => antworte('ja')),
        knopf('Nur über Umweg', () => antworte('umweg'), 'sekundaer'),
        knopf('Nein', () => antworte('nein'), 'sekundaer'),
      ),
      statusFrage,
    ),
    abschnitt(
      'Was der Browser meldet',
      liste(
        zeile('Passkey-Vorschlag im Feld (isConditionalMediationAvailable)', verfuegbar),
        zeile('conditionalGet (getClientCapabilities)', faehigkeit),
        zeile('Läuft als', modus() === 'app' ? 'installierte App' : 'Browser'),
      ),
    ),
  );

  await meldeVerfuegbarkeit(verfuegbar, faehigkeit);
  if (verfuegbar === 'ja') {
    nochmal.replaceWith(vorschlagBereitstellen(feld, status, aufraeumer));
  } else {
    setzeMeldung(status, 'Dieser Browser meldet keinen Passkey-Vorschlag im Feld. Bitte trotzdem ins Feld tippen und die Frage beantworten.');
  }
  return aufraeumer.alles;
};
